import axios from 'axios';
import { getCSRFToken } from '@leaftrade/leaftrade-component-library';

export const buildQueryStringFromParams = (params) => {
  const queryString = Object.keys(params)
    .map((key) => `${key}=${params[key]}`)
    .join('&');
  return queryString;
};

export const axiosGetData = async ({
  meta,
  onError,
  onSuccess,
  rejectWithValue,
  url,
  signal,
}) => {
  try {
    const response = await axios.get(url, signal ? { signal } : undefined);
    if (onSuccess) await onSuccess(response.data);
    if (meta) {
      return {
        ...response.data,
        ...meta,
      };
    }
    return response.data;
  } catch (error) {
    if (error.code === 'ERR_CANCELED') {
      return null;
    }
    if (onError) await onError(error);
    return rejectWithValue(error);
  }
};

export const axiosGetParallelData = async ({
  getParams,
  meta,
  onError,
  onSuccess,
  rejectWithValue,
}) => {
  const { urls, keys } = getParams;
  try {
    const responses = await Promise.all(urls.map((url) => axios.get(url)));
    const data = {};
    responses.forEach((response, index) => {
      data[keys[index]] = response.data;
    });
    if (onSuccess) await onSuccess(data);
    if (meta) {
      return {
        ...data,
        ...meta,
      };
    }
    return data;
  } catch (error) {
    if (onError) await onError(error);
    return rejectWithValue(error);
  }
};

const updateData = async ({
  data,
  meta,
  method,
  onError,
  onSuccess,
  rejectWithValue,
  url,
  headers = {},
}) => {
  try {
    const response = await axios[method](url, data, {
      headers: {
        'X-CSRFToken': getCSRFToken(),
        ...headers,
      },
      cache: 'no-cache',
    });
    if (onSuccess) await onSuccess(response.data);
    if (meta) {
      return {
        ...response.data,
        ...meta,
      };
    }
    return response.data;
  } catch (error) {
    if (onError) await onError(error);
    return rejectWithValue(error);
  }
};

export const axiosPatchData = async (kwargs) =>
  updateData({ method: 'patch', ...kwargs });
export const axiosPostData = async (kwargs) =>
  updateData({ method: 'post', ...kwargs });
export const axiosPutData = async (kwargs) =>
  updateData({ method: 'put', ...kwargs });

export const axiosDeleteData = async ({
  data,
  meta,
  onError,
  onSuccess,
  rejectWithValue,
  url,
}) => {
  try {
    const response = await axios.delete(url, {
      headers: {
        'X-CSRFToken': getCSRFToken(),
      },
      cache: 'no-cache',
      data,
    });
    if (onSuccess) await onSuccess(response.data);
    if (meta) {
      return {
        ...response.data,
        ...meta,
      };
    }
    return response.data;
  } catch (error) {
    if (onError) await onError(error);
    return rejectWithValue(error);
  }
};

export const axiosPostQueue = async ({
  url,
  data, // Array of data objects
  meta,
  headers,
  onError,
  onSuccess,
  rejectWithValue,
}) => {
  const results = {};

  try {
    for (let i = 0; i < data.length; i++) {
      const item = data[i];
      const response = await axios.post(url, item, {
        headers: {
          'X-CSRFToken': getCSRFToken(),
          ...headers,
        },
      });
      results[`item_${i}`] = meta
        ? { ...response.data, ...meta }
        : response.data;
    }

    if (onSuccess) {
      await onSuccess(results);
    }
    return results;
  } catch (error) {
    if (onError) {
      await onError(error);
    }
    if (rejectWithValue) {
      return rejectWithValue(error);
    }
    throw error;
  }
};

export const methodsMap = {
  get: axiosGetData,
  post: axiosPostData,
  put: axiosPutData,
  delete: axiosDeleteData,
};
